import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import deleteIconUrl from 'url:./canvases/delete-icon.svg'
import linkOffUrl from 'url:./canvases/link-off.svg'
import linkOnUrl from 'url:./canvases/link-on.svg'
import linkPageUrl from 'url:./canvases/link-page.svg'

const PROPERTY_OPTIONS = [
  { value: 'none', label: 'none' },
  { value: 'text', label: 'text' },
  { value: 'text-color', label: 'text-color' },
  { value: 'opacity', label: 'opacity' },
  { value: 'fill-color', label: 'fill-color' },
  { value: 'border-color', label: 'border-color' }
]

function parseTextWithHexColors (text) {
  if (Array.isArray(text)) {
    return text.map((hex, i) => (
      <div
        key={i}
        className={`hex-color-box${i === 0 ? ' first' : ''}`}
        style={{ backgroundColor: hex }}
        title={hex}
      />
    ))
  }
  if (typeof text === 'string' && text.includes('#') && text.includes(',')) {
    const colors = text.split(',').map(c => c.trim()).filter(c => c.startsWith('#'))
    if (colors.length > 0) {
      return colors.map((hex, i) => (
        <div
          key={i}
          className={`hex-color-box${i === 0 ? ' first' : ''}`}
          style={{ backgroundColor: hex }}
          title={hex}
        />
      ))
    }
  }
  return text
}

const AUTO_RECOMMEND_TOOLTIP_ID = 'auto-recommend-links-tooltip'

const TYPE_ICON = { direct: '📍', semantic: '💭', formulaic: '📐' }
const TYPE_TOOLTIP = {
  direct: 'Direct link: Directly appears on the page',
  semantic: 'Semantic link: Interpreted from the page',
  formulaic: 'Formulaic link: Transformed via custom calculations (click ⇄ to edit formula)'
}

export function LinkPanelList ({
  linkPanels,
  selectedPanelIndex,
  linkingPanelIndex,
  currentDateKey,
  recommendationsLoading,
  onPropertyTypeChange,
  onDeletePanel,
  onTogglePanelType,
  onPanelClick,
  onLinkArmToggle,
  onOpenFormula,
  onAddLink,
  onRecommendLinks,
  onExportMappingsJson
}) {
  const [recommendTipOpen, setRecommendTipOpen] = useState(false)
  const [recommendTipPos, setRecommendTipPos] = useState({ top: 0, left: 0 })
  const recommendWrapRef = useRef(null)
  const recommendPortalRef = useRef(null)

  const syncRecommendTooltipPosition = useCallback(() => {
    const wrap = recommendWrapRef.current
    if (!wrap || !recommendTipOpen) return
    const r = wrap.getBoundingClientRect()
    setRecommendTipPos({ top: r.bottom + 6, left: r.left + r.width / 2 })
  }, [recommendTipOpen])

  useLayoutEffect(() => {
    if (!recommendTipOpen) return undefined
    syncRecommendTooltipPosition()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncRecommendTooltipPosition) : null
    if (ro && recommendWrapRef.current) ro.observe(recommendWrapRef.current)
    window.addEventListener('scroll', syncRecommendTooltipPosition, true)
    window.addEventListener('resize', syncRecommendTooltipPosition)
    return () => {
      if (ro) ro.disconnect()
      window.removeEventListener('scroll', syncRecommendTooltipPosition, true)
      window.removeEventListener('resize', syncRecommendTooltipPosition)
    }
  }, [recommendTipOpen, syncRecommendTooltipPosition])

  useEffect(() => {
    if (!recommendTipOpen) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setRecommendTipOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [recommendTipOpen])

  const onRecommendWrapMouseLeave = useCallback((e) => {
    const portal = recommendPortalRef.current
    if (portal && e.relatedTarget && portal.contains(e.relatedTarget)) return
    setRecommendTipOpen(false)
  }, [])

  const onRecommendPortalMouseLeave = useCallback((e) => {
    const wrap = recommendWrapRef.current
    if (wrap && e.relatedTarget && wrap.contains(e.relatedTarget)) return
    setRecommendTipOpen(false)
  }, [])

  return (
    <>
        <div className='link-panel-list'>
          {linkPanels.map((panel, idx) => {
          const panelType = panel.type || 'direct'
          const isSelected = selectedPanelIndex === idx
          const isArmingLink = linkingPanelIndex === idx
          const hasLinkedId = Boolean(panel.linkedNodeIds && panel.linkedNodeIds[0])
          const linkIconSrc = isArmingLink ? linkPageUrl : (hasLinkedId ? linkOnUrl : linkOffUrl)
          const linkAriaLabel = isArmingLink
            ? 'Cancel link selection'
            : (hasLinkedId ? 'Change or remove SVG link' : 'Link to element on design')
          return (
            <div
              key={idx}
              className={`link-panel${isSelected ? ' link-panel--selected' : ''}`}
              style={{ border: `2px solid ${isSelected ? panel.color : 'transparent'}` }}
              onClick={() => onPanelClick && onPanelClick(idx)}
            >
              <div className='panel-leading-controls'>
                <div
                  className='panel-type-indicator'
                  title={TYPE_TOOLTIP[panelType] || TYPE_TOOLTIP.direct}
                  onClick={(e) => {
                    e.stopPropagation()
                    onTogglePanelType(idx)
                  }}
                >
                  {TYPE_ICON[panelType] || '📍'}
                </div>
                <button
                  type='button'
                  className='panel-link-icon-btn'
                  aria-pressed={isArmingLink}
                  aria-label={linkAriaLabel}
                  title={
                    isArmingLink
                      ? 'Click the design to link or unlink; Esc to cancel'
                      : (hasLinkedId
                          ? 'Link: click canvas — same element unlinks, another relinks'
                          : 'Link: then click an element on the design')
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    onLinkArmToggle && onLinkArmToggle(idx)
                  }}
                >
                  <img src={linkIconSrc} alt='' width={18} height={18} draggable={false} />
                </button>
              </div>

              {/* Content: selection box + switch icon (with reasoning badge) */}
              <div className='panel-content-container'>
                <div className='panel-content-item'>
                  {panel.text
                    ? (
                      <span
                        className='selection-box-panel'
                        style={{ backgroundColor: panel.color }}
                        title={typeof panel.text === 'string' ? panel.text : undefined}
                      >
                        {parseTextWithHexColors(panel.text)}
                      </span>
                      )
                    : (
                      <span
                        className='selection-box-panel'
                        style={{ backgroundColor: panel.color }}
                      >
                        No data
                      </span>
                      )}
                </div>

                <div className='formulaic-type-container'>
                  <div
                    className={`formulaic-type-icon${panelType === 'formulaic' ? ' formulaic-type-icon--interactive' : ''}`}
                    role={panelType === 'formulaic' ? 'button' : undefined}
                    tabIndex={panelType === 'formulaic' ? 0 : undefined}
                    title={panelType === 'formulaic' ? 'Edit formula' : undefined}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (panelType === 'formulaic' && onOpenFormula) onOpenFormula(idx)
                    }}
                    onKeyDown={(e) => {
                      if (panelType !== 'formulaic' || !onOpenFormula) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        onOpenFormula(idx)
                      }
                    }}
                  >
                    ⇄
                  </div>
                  {panel.reasoning && (
                    <div
                      className='reasoning-tooltip-wrap'
                      tabIndex={0}
                      aria-label='Reasoning for this link'
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <span className='reasoning-tooltip-trigger' aria-hidden>
                        ?
                      </span>
                      <div className='reasoning-tooltip-popover' role='tooltip'>
                        {panel.reasoning}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Property type dropdown */}
              <select
                className='panel-combobox'
                value={panel.propertyType || 'none'}
                onChange={(e) => { e.stopPropagation(); onPropertyTypeChange(idx, e.target.value) }}
                onClick={(e) => e.stopPropagation()}
              >
                {PROPERTY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <button
                type='button'
                className='panel-delete-btn'
                onClick={(e) => { e.stopPropagation(); onDeletePanel(idx) }}
                title='Delete panel'
                aria-label='Delete panel'
              >
                <img
                  className='panel-delete-btn-icon'
                  src={deleteIconUrl}
                  alt=''
                  width={18}
                  height={18}
                  draggable={false}
                />
              </button>
            </div>
          )
          })}
        </div>

      <div className='add-link-btn-row'>
        <button type='button' onClick={onAddLink}>
          Add Link
        </button>
        <div
          ref={recommendWrapRef}
          className='recommend-action-tooltip-wrap'
          onMouseEnter={() => {
            const wrap = recommendWrapRef.current
            if (wrap) {
              const r = wrap.getBoundingClientRect()
              setRecommendTipPos({ top: r.bottom + 6, left: r.left + r.width / 2 })
            }
            setRecommendTipOpen(true)
          }}
          onMouseLeave={onRecommendWrapMouseLeave}
        >
          <button
            type='button'
            onClick={onRecommendLinks}
            disabled={recommendationsLoading}
            aria-describedby={recommendTipOpen ? AUTO_RECOMMEND_TOOLTIP_ID : undefined}
          >
            {recommendationsLoading ? 'Thinking…' : 'Auto-recommend Links'}
          </button>
        </div>
        {recommendTipOpen && typeof document !== 'undefined' && createPortal(
          <div
            ref={recommendPortalRef}
            id={AUTO_RECOMMEND_TOOLTIP_ID}
            className='recommend-portal-tooltip'
            role='tooltip'
            style={{
              top: recommendTipPos.top,
              left: recommendTipPos.left
            }}
            onMouseEnter={() => setRecommendTipOpen(true)}
            onMouseLeave={onRecommendPortalMouseLeave}
          >
            Replaces all your existing link panels with AI-suggested mappings.
          </div>,
          document.body
        )}
        {/* <button
          type='button'
          onClick={onExportMappingsJson}
          title='Download linkPanels JSON (edit selectionsByDate and re-import via pregenerated files)'
        >
          Export mappings JSON
        </button> */}
      </div>
    </>
  )
}
